import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, MessageCircle, Send } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import ApilyLogo from "@/components/ApilyLogo";
import { LegalLinks } from "@/components/LegalLinks";
import {
  SUPPORT_EMAIL,
  SUPPORT_MAILTO,
  SUPPORT_TELEGRAM_BOT_URL,
  SUPPORT_TELEGRAM_CHANNEL_URL,
} from "@/const/legal";

const PLATFORM_LINKS = [
  ["models", "/models", "public_models_nav"],
  ["pricing", "/pricing", "intent_pricing_nav"],
  ["openai", "/openai-compatible-api", "intent_openai_nav"],
  ["anthropic", "/anthropic-compatible-api", "intent_anthropic_nav"],
  ["compare", "/compare-direct-api", "intent_compare_nav"],
];

const DOCS_LINKS = [
  ["docs", "/docs/getting-started", "header_docs"],
  ["guides", "/guides", "guides_nav"],
  ["codex", "/guides/codex-cli", "intent_openai_link_codex"],
  ["kilo", "/guides/kilo-code", "intent_openai_link_kilo"],
  ["docs-chat-completions", "/docs/api-chat-completions", "landing_docs_chat_completions"],
  ["docs-messages", "/docs/api-messages", "landing_docs_messages"],
];

// One footer for every screen. `onAuthOpen` switches on the landing extras (sign-up call to
// action and the SaaSHub badge); `wide` follows the docs layout width.
export function SiteFooter({ onAuthOpen, wide = false, testId = "site-footer" }) {
  const { t } = useLang();
  const year = useMemo(() => new Date().getFullYear(), []);
  const landing = typeof onAuthOpen === "function";
  const className = [
    "site-footer",
    landing ? "site-footer-landing" : "site-footer-page",
    wide ? "site-footer-wide" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <footer className={className} data-testid={testId}>
      {landing ? (
        <section className="site-footer-cta" aria-labelledby={`${testId}-cta-heading`}>
          <div className="site-footer-mark" aria-hidden="true">
            <ApilyLogo />
          </div>
          <h2 id={`${testId}-cta-heading`} className="site-footer-cta-title">
            {t("landing_footer_title")}
          </h2>
          <p className="site-footer-cta-subtitle">{t("landing_footer_subtitle")}</p>
          <button
            className="btn-primary landing-cta site-footer-cta-btn"
            onClick={() => onAuthOpen("register")}
            type="button"
            data-testid={`${testId}-cta`}
          >
            <span>{t("landing_cta")}</span>
            <ArrowRight size={16} aria-hidden="true" />
          </button>
        </section>
      ) : null}

      <div className="site-footer-columns">
        <nav className="site-footer-column" aria-labelledby={`${testId}-platform-heading`}>
          <h3 id={`${testId}-platform-heading`} className="site-footer-heading">
            {t("landing_footer_platform")}
          </h3>
          {PLATFORM_LINKS.map(([id, to, labelKey]) => (
            <Link key={id} className="site-footer-link" to={to} data-testid={`${testId}-${id}`}>
              {t(labelKey)}
            </Link>
          ))}
        </nav>

        <nav className="site-footer-column" aria-labelledby={`${testId}-docs-heading`}>
          <h3 id={`${testId}-docs-heading`} className="site-footer-heading">
            {t("header_docs")}
          </h3>
          {DOCS_LINKS.map(([id, to, labelKey]) => (
            <Link key={id} className="site-footer-link" to={to} data-testid={`${testId}-${id}`}>
              {t(labelKey)}
            </Link>
          ))}
        </nav>

        <nav className="site-footer-column" aria-labelledby={`${testId}-support-heading`}>
          <h3 id={`${testId}-support-heading`} className="site-footer-heading">
            {t("support_title")}
          </h3>
          <a className="site-footer-link site-footer-contact" href={SUPPORT_MAILTO} data-testid={`${testId}-email`}>
            <Mail size={14} aria-hidden />
            <span>{SUPPORT_EMAIL}</span>
          </a>
          <a
            className="site-footer-link site-footer-contact"
            href={SUPPORT_TELEGRAM_BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testId}-telegram-support`}
          >
            <Send size={14} aria-hidden />
            <span>{t("support_contact")}</span>
          </a>
          <a
            className="site-footer-link site-footer-contact"
            href={SUPPORT_TELEGRAM_CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testId}-telegram-channel`}
          >
            <MessageCircle size={14} aria-hidden />
            <span>{t("support_channel")}</span>
          </a>
        </nav>

        <div className="site-footer-column">
          <h3 className="site-footer-heading">{t("legal_section_title")}</h3>
          <LegalLinks className="site-footer-legal" testId={`${testId}-legal`} />
        </div>
      </div>

      <div className="site-footer-bottom">
        <div className="site-footer-brand">
          <ApilyLogo wordmark />
          <p className="site-footer-copy">{t("app_footer_copyright", { year })}</p>
        </div>
        {landing ? (
          <a
            className="site-footer-badge"
            href="https://www.saashub.com/apily?utm_source=badge&utm_campaign=badge&utm_content=apily&badge_variant=dark&badge_kind=approved"
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testId}-saashub-badge`}
          >
            <img
              src="https://cdn-b.saashub.com/img/badges/approved-dark.png?v=1"
              alt={t("landing_saashub_badge_alt")}
              width={150}
              height={50}
              loading="lazy"
              decoding="async"
            />
          </a>
        ) : null}
      </div>
    </footer>
  );
}
